/**
 * What a connection row says (RidgeWill/account#79).
 *
 * A module rather than JSX for the reason `lib/subscriptions.ts` gives: every
 * sentence here is a claim about a relationship between two people, and a pure
 * function can be tested against every shape the service sends where a
 * component is only tested against the shapes somebody thought to render.
 *
 * ── NO PRODUCT NAMES, ANYWHERE IN THIS SECTION ───────────────────────
 *
 * account#79: if this section names a product anywhere, the declaration
 * mechanism has failed and the next app needs a portal change to ship. A
 * connection is a platform relationship, so everything here speaks about
 * "Ridgewill apps" and never about one of them. `Connections.test.tsx` reads the
 * source to hold that.
 */
import { formatDay } from './subscriptions.js';

/** The other person, as auth's portal endpoints send them. */
export interface ConnectionPerson {
  name: string;
  username: string | null;
  image: string | null;
}

/**
 * One row from `GET /account/connections` or `GET /account/blocks`
 * (RidgeWill/auth#486): the platform's connection view plus the other person.
 *
 * Dates are ISO strings on the wire, never `Date`s.
 */
export interface PortalConnection {
  id: string;
  state: 'pending' | 'active' | 'dropped' | 'blocked';
  /** The other account, or null while an invitation is outstanding. */
  userId: string | null;
  /** The invited address, sent to the inviter only. */
  invitedEmail: string | null;
  blockedByMe: boolean;
  droppedByMe: boolean;
  initiatedByMe: boolean;
  establishedAt: string | null;
  invitationExpiresAt: string | null;
  /**
   * Both people are in the same Ridgewill Family Group (RidgeWill/auth#614).
   * Such a connection cannot be removed from here: the way apart is leaving the
   * Family Group, or a guardian removing the dependent. Absent from an older
   * auth, which reads as false.
   */
  family?: boolean;
  /**
   * On a Family connection, who leaves the shared Family Group if you block
   * them (RidgeWill/auth#655): `you-leave` or `they-leave`. The Block dialog says
   * which. Absent from an older auth.
   */
  familyOnBlock?: 'you-leave' | 'they-leave' | null;
  /** Null on an outstanding invitation, where there is nobody yet. */
  person: ConnectionPerson | null;
}

export interface ConnectionLabel {
  /** Who this row is about, in words. Never empty. */
  title: string;
  /** `@username` under the title, or null when there is nothing to add. */
  handle: string | null;
}

/**
 * How to name the other end of a row.
 *
 * The name first, because that is what people recognise each other by. A
 * closed account can come back with an empty name, and an invitation has no
 * account at all, so each gap has a fallback that is still a true sentence
 * rather than a blank row.
 */
export function connectionLabel(c: PortalConnection): ConnectionLabel {
  const name = c.person?.name?.trim() ?? '';
  const username = c.person?.username?.trim() || null;
  if (name) return { title: name, handle: username ? `@${username}` : null };
  if (username) return { title: `@${username}`, handle: null };
  if (c.state === 'pending' && c.invitedEmail) return { title: c.invitedEmail, handle: null };
  return { title: 'A Ridgewill account', handle: null };
}

/** The letter in the round placeholder when there is no picture. */
export function initialOf(label: ConnectionLabel): string {
  const letter = label.title.replace(/^@/, '').trim().charAt(0);
  return letter ? letter.toUpperCase() : '?';
}

/**
 * The sentence under a row, or null when there is nothing true to add.
 *
 * `format` is injectable because `toLocaleDateString` answers differently
 * wherever the test runs; see `formatDay`.
 */
export function connectionDetail(
  c: PortalConnection,
  format: (iso: string) => string = formatDay,
): string | null {
  if (c.state === 'pending') {
    const to = c.invitedEmail ? `Invitation sent to ${c.invitedEmail}` : 'Invitation not accepted yet';
    return c.invitationExpiresAt ? `${to}. It stays open until ${format(c.invitationExpiresAt)}.` : `${to}.`;
  }
  if (c.state === 'blocked') {
    return 'They cannot connect with you or invite you in any Ridgewill app.';
  }
  if (c.establishedAt) return `Connected since ${format(c.establishedAt)}.`;
  return null;
}

/**
 * ── ONE CONNECTION HAS A PAGE OF ITS OWN ─────────────────────────────
 *
 * `/connections/<id>` (account#79). It is what a product links to when it means
 * one person rather than the whole list, and it is where settings about a single
 * relationship belong.
 *
 * Products used to link to that person's ROW, `/connections#connection-<id>`,
 * and those links are already shipped. `connectionPageFor` forwards them here, so
 * no product has to change and an old link to a row nobody holds any more says
 * so instead of landing silently at the top of the list.
 */
const PAGE_PREFIX = '/connections/';
const ROW_ANCHOR = '#connection-';

export function connectionPath(id: string): string {
  return `${PAGE_PREFIX}${encodeURIComponent(id)}`;
}

/** The connection id a path names, or null when it names none. */
export function connectionIdOf(path: string): string | null {
  if (!path.startsWith(PAGE_PREFIX)) return null;
  const rest = path.slice(PAGE_PREFIX.length).replace(/\/+$/, '');
  if (!rest || rest.includes('/')) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    // A malformed escape is a link that cannot name anything.
    return null;
  }
}

/** Where an old row link should go instead, or null when it is not one. */
export function connectionPageFor(pathname: string, hash: string): string | null {
  if (pathname.replace(/\/+$/, '') !== '/connections' || !hash.startsWith(ROW_ANCHOR)) return null;
  const id = hash.slice(ROW_ANCHOR.length);
  return id ? `${PAGE_PREFIX}${id}` : null;
}

/**
 * What the page for `id` has to show, from the two lists the portal already
 * loads. There is no single-connection read in auth, and one is not needed:
 * both lists are the reader's own and already small.
 *
 * `missing` covers three different histories on purpose: a removal by either
 * person, a block by the OTHER person, and a link that never named anything.
 * auth sends no row for the second, so the page cannot tell it from the first,
 * and that is the block rule working (Jon, 2026-09-13).
 */
export type FoundConnection =
  | { kind: 'connection'; connection: PortalConnection }
  | { kind: 'blocked'; connection: PortalConnection }
  | { kind: 'missing' };

export function findConnection(
  id: string,
  connections: PortalConnection[],
  blocks: PortalConnection[],
): FoundConnection {
  const connection = connections.find((c) => c.id === id);
  if (connection) return { kind: 'connection', connection };
  const blocked = blocks.find((b) => b.id === id);
  if (blocked) return { kind: 'blocked', connection: blocked };
  return { kind: 'missing' };
}
